﻿package org.zq.pixelset.platform

import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.graphics.toComposeImageBitmap
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.suspendCancellableCoroutine
import org.jetbrains.skia.Image as SkiaImage
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Uint8Array
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLImageElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.url.URL
import org.w3c.files.Blob
import org.w3c.files.File
import org.w3c.files.FileReader
import org.zq.pixelset.model.DecodedImage
import org.zq.pixelset.model.OutputFormat
import org.zq.pixelset.model.PickedImage
import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException
import kotlin.js.Promise

actual object ImagePlatform {
    actual fun isWebPlatform(): Boolean = true

    actual suspend fun pickImages(): List<PickedImage> = suspendCancellableCoroutine { cont ->
        val input = document.createElement("input") as HTMLInputElement
        input.type = "file"
        input.accept = "image/jpeg,image/png,image/webp,image/gif"
        input.multiple = true
        input.asDynamic().style.display = "none"
        document.body?.appendChild(input)
        input.onchange = {
            val files = input.files
            if (files == null || files.length == 0) {
                document.body?.removeChild(input)
                cont.resume(emptyList())
            } else {
                val promises = (0 until files.length).map { i ->
                    readFile(files.item(i)!!)
                }
                Promise.all(promises.toTypedArray()).then { results ->
                    document.body?.removeChild(input)
                    @Suppress("UNCHECKED_CAST")
                    cont.resume((results as Array<PickedImage>).toList())
                }.catch { err ->
                    document.body?.removeChild(input)
                    cont.resumeWithException(Exception(err.toString()))
                }
            }
        }
        input.click()
    }

    actual suspend fun decodeImage(bytes: ByteArray): DecodedImage {
        val blob = Blob(arrayOf(bytes.toUint8Array()))
        val url = URL.createObjectURL(blob)
        return try {
            loadImageFromUrl(url)
        } finally {
            URL.revokeObjectURL(url)
        }
    }

    actual fun toImageBitmap(image: DecodedImage): ImageBitmap {
        val canvas = document.createElement("canvas") as HTMLCanvasElement
        canvas.width = image.width
        canvas.height = image.height
        val ctx = canvas.getContext("2d") as org.w3c.dom.CanvasRenderingContext2D
        val imageData = ctx.createImageData(image.width.toDouble(), image.height.toDouble())
        writePixelsToImageData(imageData, image.pixels)
        ctx.putImageData(imageData, 0.0, 0.0)
        return canvasToImageBitmap(canvas)
    }

    actual suspend fun encodeImage(
        image: DecodedImage,
        format: OutputFormat,
        quality: Float,
    ): ByteArray = suspendCancellableCoroutine { cont ->
        val canvas = document.createElement("canvas") as HTMLCanvasElement
        canvas.width = image.width
        canvas.height = image.height
        val ctx = canvas.getContext("2d") as? org.w3c.dom.CanvasRenderingContext2D ?: run {
            cont.resumeWithException(IllegalStateException("Canvas 不可用"))
            return@suspendCancellableCoroutine
        }
        val imageData = ctx.createImageData(image.width.toDouble(), image.height.toDouble())
        writePixelsToImageData(imageData, image.pixels)
        ctx.putImageData(imageData, 0.0, 0.0)
        val mime = when (format) {
            OutputFormat.PNG -> "image/png"
            OutputFormat.JPG -> "image/jpeg"
            OutputFormat.WEBP -> "image/webp"
        }
        canvas.toBlob({ blob ->
            if (blob == null) {
                cont.resumeWithException(IllegalStateException("编码失败"))
            } else {
                readBlob(blob).then { arr ->
                    cont.resume(Uint8Array(arr).toByteArray())
                }
            }
        }, mime, quality.coerceIn(0.1f, 1.0f).toDouble())
    }

    actual fun downloadFile(bytes: ByteArray, fileName: String) {
        triggerDownload(bytes, fileName)
    }

    actual fun downloadFiles(files: List<Pair<String, ByteArray>>) {
        files.forEachIndexed { index, (name, bytes) ->
            window.setTimeout({
                triggerDownload(bytes, name)
            }, index * 300)
        }
    }

    private fun triggerDownload(bytes: ByteArray, fileName: String) {
        val blob = Blob(arrayOf(bytes.toUint8Array()))
        val url = URL.createObjectURL(blob)
        val anchor = document.createElement("a") as HTMLAnchorElement
        anchor.href = url
        anchor.download = fileName
        document.body?.appendChild(anchor)
        anchor.click()
        document.body?.removeChild(anchor)
        URL.revokeObjectURL(url)
    }

    private fun readFile(file: File): Promise<PickedImage> = Promise { resolve, reject ->
        val reader = FileReader()
        reader.onload = {
            val buffer = reader.result
            if (buffer is ArrayBuffer) {
                resolve(
                    PickedImage(
                        name = file.name,
                        bytes = Uint8Array(buffer as ArrayBuffer).toByteArray(),
                        sizeBytes = file.size.toLong(),
                    ),
                )
            } else {
                reject(Exception("读取文件失败"))
            }
        }
        reader.onerror = { reject(Exception("读取文件失败")) }
        reader.readAsArrayBuffer(file)
    }

    private suspend fun loadImageFromUrl(url: String): DecodedImage = suspendCancellableCoroutine { cont ->
        val img = document.createElement("img") as HTMLImageElement
        img.onload = {
            val canvas = document.createElement("canvas") as HTMLCanvasElement
            canvas.width = img.naturalWidth
            canvas.height = img.naturalHeight
            val ctx = canvas.getContext("2d") as org.w3c.dom.CanvasRenderingContext2D
            ctx.drawImage(img, 0.0, 0.0)
            val imageData = ctx.getImageData(0.0, 0.0, canvas.width.toDouble(), canvas.height.toDouble())
            val pixels = readPixelsFromImageData(imageData)
            cont.resume(DecodedImage(canvas.width, canvas.height, pixels))
        }
        img.asDynamic().onerror = { _: dynamic ->
            cont.resumeWithException(Exception("图片加载失败"))
            null
        }
        img.src = url
    }

    private fun readBlob(blob: Blob): Promise<ArrayBuffer> = Promise { resolve, reject ->
        val reader = FileReader()
        reader.onload = { resolve(reader.result as ArrayBuffer) }
        reader.onerror = { reject(Exception("读取 Blob 失败")) }
        reader.readAsArrayBuffer(blob)
    }
}

private fun writePixelsToImageData(imageData: org.w3c.dom.ImageData, pixels: IntArray) {
    val data = imageData.data.asDynamic()
    for (i in pixels.indices) {
        val argb = pixels[i]
        val base = i * 4
        data[base] = (argb shr 16) and 0xFF
        data[base + 1] = (argb shr 8) and 0xFF
        data[base + 2] = argb and 0xFF
        data[base + 3] = (argb shr 24) and 0xFF
    }
}

private fun readPixelsFromImageData(imageData: org.w3c.dom.ImageData): IntArray {
    val data = imageData.data.asDynamic()
    val count = imageData.width * imageData.height
    val pixels = IntArray(count)
    for (i in 0 until count) {
        val base = i * 4
        val r = (data[base] as Int) and 0xFF
        val g = (data[base + 1] as Int) and 0xFF
        val b = (data[base + 2] as Int) and 0xFF
        val a = (data[base + 3] as Int) and 0xFF
        pixels[i] = (a shl 24) or (r shl 16) or (g shl 8) or b
    }
    return pixels
}

private fun ByteArray.toUint8Array(): Uint8Array {
    val arr = Uint8Array(size)
    val dynamic = arr.asDynamic()
    for (i in indices) dynamic[i] = this[i]
    return arr
}

private fun Uint8Array.toByteArray(): ByteArray {
    val dynamic = asDynamic()
    return ByteArray(length) { (dynamic[it] as Number).toInt().toByte() }
}

private fun canvasToImageBitmap(canvas: HTMLCanvasElement): ImageBitmap {
    val dataUrl = canvas.toDataURL("image/png")
    val base64 = dataUrl.substringAfter("base64,")
    val binary = window.atob(base64) as String
    val bytes = ByteArray(binary.length) { binary[it].code.toByte() }
    return SkiaImage.makeFromEncoded(bytes).toComposeImageBitmap()
}
